<script>
import { mapGetters } from 'vuex';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import axios from 'axios';

export default {
  components: { 
    SettingsLayout, 
    BaseSettingsHeader,
    Button
  },
  data() {
    return {
      loading: false,
      calendarConnected: false,
      error: null,
      integration: {
        name: 'Agenda do Google',
        description: 'Integre sua Agenda do Google para visualizar e gerenciar eventos',
      },
      updatingIntegration: false,
    };
  },
  created() {
    // Check if returning from OAuth callback with a success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('calendar_connected')) {
      this.calendarConnected = urlParams.get('calendar_connected') === 'true';

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    this.verificaIntegracao();
  },
  methods: {
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    },
    getAuthHeaders() {
      const cookie = this.getCookie('cw_d_session_info');
      if (!cookie) {
        console.error('cw_d_session_info cookie não encontrado');
        return {};
      }
      try {
        const sessionInfo = JSON.parse(decodeURIComponent(cookie));
        return {
          'access-token': sessionInfo['access-token'],
          client: sessionInfo.client,
          uid: sessionInfo.uid,
        };
      } catch (e) {
        console.error('Erro ao analisar cw_d_session_info:', e);
        return {};
      }
    },
    async verificaIntegracao() {
      this.loading = true;
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar' },
        });

        // Check if there's an error from the server
        if (response.data && response.data.error) {
          this.error = response.data.error;
          this.calendarConnected = false;
          return;
        }

        // Check for the active property in the response
        if (response.data && typeof response.data.active === 'boolean') {
          this.calendarConnected = response.data.active;
        } else if (response.data && Array.isArray(response.data.integrations)) {
          // If we got a list of integrations, check if Google Calendar is enabled
          const googleCalendar = response.data.integrations.find(i => i.id === 'google_calendar');
          this.calendarConnected = googleCalendar && googleCalendar.active === true;
        } else {
          this.calendarConnected = false;
        }
      } catch (err) {
        console.error('Erro na verificação da integração:', err);
        this.calendarConnected = false;
        if (err.response && err.response.status !== 401) {
          this.error = 'Falha ao verificar status da integração. Tente novamente.';
        }
      } finally {
        this.loading = false;
      }
    },
    async connectCalendar() {
      this.loading = true;
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar' },
        });

        // Check for server-side errors
        if (response.data && response.data.error) {
          throw new Error(response.data.error);
        }

        // Check if we have an authorization URL
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('URL de autorização não encontrada na resposta');
        }
      } catch (err) {
        console.error('Falha ao conectar à Agenda do Google:', err);
        this.error = 'Falha ao conectar. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },
    async updateCalendarIntegration() {
      this.updatingIntegration = true;
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar', update: true },
        });

        // Check for server-side errors
        if (response.data && response.data.error) {
          throw new Error(response.data.error);
        }

        // Check if we have an authorization URL
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('URL de autorização de atualização não encontrada na resposta');
        }
      } catch (err) {
        console.error('Falha ao atualizar a integração com a Agenda do Google:', err);
        this.error = 'Falha ao atualizar a integração. Tente novamente.';
      } finally {
        this.updatingIntegration = false;
      }
    },
    async disconnectCalendar() {
      this.loading = true;
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.delete('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
        });

        if (response.status === 200) {
          this.calendarConnected = false;
        } else {
          throw new Error('Falha ao desconectar a Agenda do Google');
        }
      } catch (err) {
        console.error('Erro ao desconectar a Agenda do Google:', err);
        this.error = 'Falha ao desconectar. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },
    // Handle primary action based on connection status
    handleMainAction() {
      if (this.calendarConnected) {
        this.disconnectCalendar();
      } else {
        this.connectCalendar();
      }
    }
  },
  computed: {
    statusMessage() {
      return this.calendarConnected
        ? 'Sua Agenda do Google está conectada. Você pode visualizar seus eventos do Google Calendar em nosso aplicativo.'
        : 'Conecte sua Agenda do Google para visualizar e gerenciar seus eventos do Google Calendar em nosso aplicativo.';
    },
    buttonLabel() {
      return this.calendarConnected ? 'Desconectar Agenda' : 'Conectar Agenda';
    },
    buttonIcon() {
      return this.calendarConnected ? 'i-lucide-link-2-off' : 'i-lucide-link';
    },
    buttonColor() {
      return this.calendarConnected ? 'ruby' : 'blue';
    }
  }
};
</script>

<template>
  <SettingsLayout :is-loading="loading" loading-message="Verificando integração...">
    <template #header>
      <BaseSettingsHeader :title="integration.name" :description="integration.description"
        back-button-label="Voltar para Configurações">
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="integration-container">
        <div class="integration-card">
          <!-- Error display -->
          <div v-if="error" class="flex items-center p-4 mb-6 text-n-ruby-11 bg-n-ruby-9/10 rounded-lg w-full">
            <span class="i-lucide-alert-circle mr-2"></span>
            {{ error }}
          </div>

          <!-- Connection status indicator -->
          <div class="status-container">
            <div class="status-icon-wrapper" :class="calendarConnected ? 'connected' : 'disconnected'">
              <span v-if="calendarConnected" class="i-lucide-check text-3xl"></span>
              <span v-else class="i-lucide-alert-circle text-3xl"></span>
            </div>

            <h3 class="status-title">
              {{ calendarConnected ? 'Agenda do Google Conectada' : 'Agenda do Google Não Conectada' }}
            </h3>
            <p class="status-message">{{ statusMessage }}</p>
          </div>

          <!-- Action buttons -->
          <div class="action-buttons">
            <!-- Connect/Disconnect button -->
            <Button
              :label="buttonLabel"
              :icon="buttonIcon"
              :color="buttonColor"
              :is-loading="loading"
              :disabled="loading"
              @click="handleMainAction"
            />

            <!-- Update permissions button (only shown when connected) -->
            <Button
              v-if="calendarConnected"
              label="Atualizar Permissões"
              color="slate"
              variant="outline"
              :is-loading="updatingIntegration"
              :disabled="updatingIntegration || loading"
              @click="updateCalendarIntegration"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style>
.integration-container {
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 200px);
  background-color: var(--n-background);
}

/* Card styling */
.integration-card {
  background-color: var(--n-solid-1);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  width: 100%;
  max-width: 500px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s ease;
}

.dark .integration-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

/* Status styling */
.status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
}

.status-icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.status-icon-wrapper.connected {
  background-color: rgba(28, 185, 84, 0.1);
  color: #1cb954;
}

.status-icon-wrapper.disconnected {
  background-color: rgba(43, 112, 224, 0.1);
  color: var(--n-brand);
}

.status-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--n-slate-12);
}

.status-message {
  color: var(--n-slate-11);
  margin: 0;
  line-height: 1.5;
  max-width: 400px;
}

/* Button container */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 300px;
}

/* Responsive styles */
@media (max-width: 768px) {
  .integration-container {
    padding: 1.5rem;
  }
  
  .integration-card {
    padding: 2rem 1.5rem;
  }
}

@media (max-width: 480px) {
  .integration-container {
    padding: 1rem;
  }
  
  .integration-card {
    padding: 1.5rem 1rem;
  }
  
  .status-title {
    font-size: 1.1rem;
  }
}
</style>